// Formatting utilities for numbers and values
// Uses d3-format for locale-aware number formatting

import { formatLocale, type FormatLocaleDefinition } from 'd3-format';
import type { FormatLocale } from './tenantConfig.service';

const DEFAULT_LOCALE: FormatLocale = {
  decimal: '.',
  thousands: ',',
  grouping: [3],
  currency: ['$', ''],
};

let _activeLocale: FormatLocale = DEFAULT_LOCALE;

/** Called by FormatLocaleSync to update the active locale from tenant config. */
export const setFormatLocale = (locale: FormatLocale) => {
  _activeLocale = locale;
};

export const getFormatLocale = (): FormatLocale => _activeLocale;

const BUILTIN_FORMATS: Record<string, { spec: string; label: string }> = {
  FINANCIAL:        { spec: '$,.2f',  label: 'Financial' },
  CURRENCY:         { spec: '$,.2f',  label: 'Currency' },
  PERCENT:          { spec: '.2%',    label: 'Percent' },
  INTEGER:          { spec: ',.0f',   label: 'Integer' },
  NUMERIC:          { spec: ',.2f',   label: 'Numeric' },
  NUMBER:           { spec: ',.2f',   label: 'Number' },
  DECIMAL:          { spec: ',.2f',   label: 'Decimal' },
  MULTIPLIER:       { spec: '.2f',    label: 'Multiplier' },
  SUMMARIZE_NUMBER:    { spec: '.3~s',   label: 'Summarized' },
  SUMMARIZE_FINANCIAL: { spec: '.3~s',   label: 'Summarized Financial' },
};

/** Get the list of available display formats for dropdowns. */
export const getAvailableFormats = (): { value: string; label: string }[] => {
  // Return a curated subset (exclude aliases like CURRENCY/NUMBER)
  return [
    { value: 'NUMERIC',          label: BUILTIN_FORMATS.NUMERIC.label },
    { value: 'INTEGER',          label: BUILTIN_FORMATS.INTEGER.label },
    { value: 'DECIMAL',          label: BUILTIN_FORMATS.DECIMAL.label },
    { value: 'FINANCIAL',        label: BUILTIN_FORMATS.FINANCIAL.label },
    { value: 'PERCENT',          label: BUILTIN_FORMATS.PERCENT.label },
    { value: 'MULTIPLIER',       label: BUILTIN_FORMATS.MULTIPLIER.label },
    { value: 'SUMMARIZE_NUMBER',    label: BUILTIN_FORMATS.SUMMARIZE_NUMBER.label },
    { value: 'SUMMARIZE_FINANCIAL', label: BUILTIN_FORMATS.SUMMARIZE_FINANCIAL.label },
  ];
};

/** Build a d3-format locale formatter from the active locale. */
const getLocaleFormatter = () => {
  const locale = _activeLocale;
  return formatLocale(locale as FormatLocaleDefinition);
};

/** Remap d3's SI prefix 'G' (giga) to 'B' (billions). */
const remapSIPrefix = (s: string): string => s.replace(/G$/, 'B');

export const integerFormatter = (num: number): string => {
  if (num == null) {
    return '-';
  }
  if (isNaN(num)) {
    return num.toString();
  }
  const fmt = getLocaleFormatter();
  return fmt.format(',.0f')(Math.abs(num));
};

export const decimalFormatter = (num: number): string => {
  if (num == null || isNaN(num)) {
    return num?.toString() || '-';
  }
  const fmt = getLocaleFormatter();
  return fmt.format(',.2f')(num);
};

export const formatFinancial = (num: number | string): string => {
  if (typeof num === 'string') {
    num = parseFloat(num);
  }
  if (num == null) {
    return '-';
  }
  if (isNaN(num)) {
    return num.toString();
  }

  const fmt = getLocaleFormatter();
  const absFormatted = fmt.format('$,.2f')(Math.abs(num));

  if (num < 0) {
    return '(' + absFormatted + ')';
  }
  return absFormatted;
};

export const formatPercentage = (num: number): string => {
  if (num == null) {
    return '-';
  }
  if (isNaN(num)) {
    return num.toString();
  }
  const fmt = getLocaleFormatter();
  return fmt.format('.2%')(num);
};

export const summarizeNumber = (num: number): string => {
  if (num == null) {
    return '';
  }
  if (num < 1000) {
    return num.toString();
  } else if (num < 100000) {
    return ((Math.round(num / 100)) / 10).toString() + 'k';
  } else if (num < 1000000) {
    return Math.floor(num / 1000).toString() + 'k';
  } else if (num < 1000000000) {
    return (Math.round(num / 100000) / 10).toString() + 'M';
  } else if (num < 1000000000000) {
    return (Math.floor(num / 100000000) / 10).toString() + 'B';
  }
  return '';
};

interface FormatProps {
  value: any;
  format?: string;
}

export const formatValue = ({ value, format }: FormatProps): string => {
  if (value == null) {
    return '-';
  }

  const fmt = (format || '').toUpperCase();
  const builtIn = BUILTIN_FORMATS[fmt];

  if (!builtIn) {
    return value?.toString() || '-';
  }

  const localeFmt = getLocaleFormatter();

  switch (fmt) {
    case 'FINANCIAL':
    case 'CURRENCY':
      return formatFinancial(value);
    case 'MULTIPLIER': {
      const formatted = localeFmt.format(builtIn.spec)(value);
      return formatted + 'x';
    }
    case 'SUMMARIZE_NUMBER':
      return remapSIPrefix(localeFmt.format(builtIn.spec)(value));
    case 'SUMMARIZE_FINANCIAL': {
      const summarized = remapSIPrefix(localeFmt.format(builtIn.spec)(Math.abs(value)));
      const [prefix, suffix] = _activeLocale.currency;
      const withCurrency = prefix + summarized + suffix;
      return value < 0 ? '(' + withCurrency + ')' : withCurrency;
    }
    case 'PERCENT':
      return formatPercentage(value);
    default:
      return localeFmt.format(builtIn.spec)(value);
  }
};

/**
 * Format a value for use as a tick label on a chart axis.
 * Uses summarizeNumber for most formats (has specific bracket thresholds).
 * For PERCENT, multiplies by 100, summarizes, then adds "%".
 */
export const formatTickValue = ({ value, format }: FormatProps): string => {
  if (value == null || isNaN(value)) {
    return value?.toString() || '-';
  }

  const fmt = (format || '').toUpperCase();
  switch (fmt) {
    case 'PERCENT': {
      const percentValue = value * 100;
      const summarized = summarizeNumber(percentValue);
      return summarized + ' %';
    }
    case 'FINANCIAL':
    case 'CURRENCY':
    case 'NUMERIC':
    case 'NUMBER':
    case 'DECIMAL':
    case 'INTEGER':
    case 'SUMMARIZE_NUMBER':
      return summarizeNumber(value);
    case 'SUMMARIZE_FINANCIAL': {
      const [prefix, suffix] = _activeLocale.currency;
      return prefix + summarizeNumber(Math.abs(value)) + suffix;
    }
    default:
      return summarizeNumber(value);
  }
};
